import { Text } from '@/shared/ui';
import { Meter } from '@/shared/ui/components/basic/Meter';
import type { MeterView } from '@/shared/utils/plan-tab-model';
import { PlanIcon } from './plan-tab.icons';

interface Props {
  meter: MeterView;
  label: string;
  /** "restantes" / "left", already pluralized against the remainder. */
  unit: string;
  /** "740 de 800 usadas", the reconciliation line. */
  usedOf: string;
  /**
   * The free-period legend ("Período de teste · no Rubi: 800 por mês"). When
   * set, the card reads "37 / 200" with this legend instead of the remainder:
   * the ceiling is the trial's, not the plan's (RN-06).
   */
  trialLegend?: string;
  compact?: boolean;
}

/**
 * One quota, read as what is left.
 *
 * The big figure is the remainder because that is the number that decides an
 * action; the spend is a reconciliation and sits underneath in small print.
 *
 * @param props - Component props.
 * @returns The meter card.
 */
export function MeterCard({
  meter, label, unit, usedOf, trialLegend, compact = false,
}: Props) {
  const Icon = PlanIcon[meter.key];

  return (
    <div
      className="flex flex-col gap-(--space-2-5) min-w-0"
      data-testid={`meter-${meter.key}`}
      data-trial={trialLegend ? 'true' : undefined}
      style={{
        padding: 'var(--space-4)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--surface-raised)',
      }}
    >
      <div className="flex items-center gap-(--space-2)">
        <Text component="span" className="flex" style={{ flex: 'none', color: meter.tone }}>
          <Icon size={15} strokeWidth={1.75} aria-hidden />
        </Text>
        <Text component="span" className="min-w-0 flex-1" style={{
          fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)',
          lineHeight: 'var(--leading-snug)',
        }}>
          {label}
        </Text>
      </div>
      <div className="flex items-baseline gap-(--space-1-5)">
        <Text component="span" data-testid={`meter-${meter.key}-remaining`} style={{
          fontSize: compact ? 'var(--text-2xl)' : 'var(--text-3xl)',
          fontWeight: 'var(--font-bold)',
          lineHeight: 'var(--leading-tight)',
          letterSpacing: 'var(--tracking-tight)',
          color: meter.tone,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {trialLegend ? `${meter.used} / ${meter.total}` : meter.remaining}
        </Text>
        {!trialLegend && (
          <Text component="span" style={{
            fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-semibold)',
            color: 'var(--text-tertiary-soft)',
          }}>
            {unit}
          </Text>
        )}
      </div>
      <Meter
        ratio={meter.ratio}
        tone={meter.tone}
        label={label}
        used={meter.used}
        total={meter.total}
        testId={`meter-${meter.key}-bar`}
      />
      <Text component="span" style={{
        fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)',
        lineHeight: 'var(--leading-snug)',
      }}>
        {trialLegend ?? usedOf}
      </Text>
    </div>
  );
}
